import {
  ChevronDoubleLeft,
  ChevronDoubleRight,
  ChevronLeft,
  ChevronRight,
} from "react-bootstrap-icons";

interface PaginationProps {
  recordsPerPage?: number;
  totalRecords: number;
  currentPageState: [number, (page: number) => void];
}

export default function Pagination({
  totalRecords,
  recordsPerPage = 5,
  currentPageState,
}: PaginationProps) {
  const [currentPage, setCurrentPage] = currentPageState;

  // If there's no data, return an empty fragment
  if (totalRecords === 0) return <></>;

  let totalPage = Math.ceil(totalRecords / recordsPerPage);
  let numbers = Array.from({ length: totalPage }, (_, index) => index + 1);

  const isPaginationDisabled = totalPage <= 1;

  function prevPage() {
    if (currentPage !== 1 && !isPaginationDisabled) {
      setCurrentPage(currentPage - 1);
    }
  }

  function changeCPage(n: number) {
    if (!isPaginationDisabled) {
      setCurrentPage(n);
    }
  }

  function nextPage() {
    if (currentPage !== totalPage && !isPaginationDisabled) {
      setCurrentPage(currentPage + 1);
    }
  }

  // Calculate the first and last index of the current page
  const firstIndex = (currentPage - 1) * recordsPerPage;
  const lastIndex = Math.min(firstIndex + recordsPerPage - 1, totalRecords - 1);

  // Modify the numbers array generation to handle pagination with ellipsis
  let displayNumbers: (number | string)[] = [];
  if (totalPage <= 5) {
    displayNumbers = numbers;
  } else {
    if (currentPage <= 3) {
      displayNumbers = [...numbers.slice(0, 4), '...', totalPage];
    } else if (currentPage >= totalPage - 2) {
      displayNumbers = [1, '...', ...numbers.slice(totalPage - 4)];
    } else {
      displayNumbers = [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPage];
    }
  }

  return (
    <>
      <div className="clearfix"></div>
      <div className="pagination-index col-auto float-end">
        {firstIndex + 1} - {lastIndex + 1} of {totalRecords}
      </div>
      <nav aria-label="Page navigation mt-5">
        <ul className="pagination justify-content-center">
          <li className={`page-item ${isPaginationDisabled ? 'disabled' : ''}`}
           style={{ cursor: isPaginationDisabled || currentPage === 1 ? 'not-allowed' : 'pointer' }}>
            <i className="page-link border-0 bg-transparent text-dark">
              <span
                onClick={() => !isPaginationDisabled && setCurrentPage(1)}
                style={{ opacity: isPaginationDisabled || currentPage === 1 ? 0.2 : 1, cursor: isPaginationDisabled || currentPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                <ChevronDoubleLeft size={14} />
              </span>
            </i>
          </li>
          <li className={`page-item me-3 ${isPaginationDisabled || currentPage === 1 ? 'disabled' : ''}`}
          style={{ cursor: isPaginationDisabled || currentPage === 1 ? 'not-allowed' : 'pointer' }}>
            <i className="page-link border-0 rounded bg-white text-dark "
               style={{ boxShadow: isPaginationDisabled || currentPage === 1 ? 'none' : 'var(--bs-box-shadow)' }}>
              <span
                onClick={prevPage}
                style={{ opacity: isPaginationDisabled || currentPage === 1 ? 0.2 : 1, cursor: isPaginationDisabled || currentPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                <ChevronLeft size={14} />
              </span>
            </i>
          </li>
          {displayNumbers.map((n, i) => (
            <li
              className={`page-item ${currentPage === n ? `active` : ``}`}
              aria-current="page"
              key={i}
            >
              <span
                className={`page-link border-0 rounded ${
                  n === '...' ? 'bg-transparent' : currentPage === n ? 'bg-dark' : 'bg-transparent'
                } text-${currentPage === n ? 'white' : 'dark'}`}
                onClick={() => {
                  if (typeof n === 'number') {
                    changeCPage(n);
                  }
                }}
              >
                {n}
              </span>
            </li>
          ))}
          <li className={`page-item ms-3 ${isPaginationDisabled || currentPage === totalPage ? 'disabled' : ''}`}
          style={{ cursor: isPaginationDisabled || currentPage === totalPage ? 'not-allowed' : 'pointer' }}>
            <i className="page-link border-0 rounded bg-white text-dark"
            style={{ boxShadow: isPaginationDisabled || currentPage === totalPage ? 'none' : 'var(--bs-box-shadow)' }}>
              <span
                onClick={nextPage}
                style={{ opacity: isPaginationDisabled || currentPage === totalPage ? 0.2 : 1, cursor: isPaginationDisabled || currentPage === totalPage ? 'not-allowed' : 'pointer' }}
              >
                <ChevronRight size={14} />
              </span>
            </i>
          </li>
          <li className={`page-item ${isPaginationDisabled || currentPage === totalPage ? 'disabled' : ''}`}
          style={{ cursor: isPaginationDisabled || currentPage === totalPage ? 'not-allowed' : 'pointer' }}>
            <i className="page-link border-0 bg-transparent text-dark">
              <span
                onClick={() => !isPaginationDisabled && setCurrentPage(totalPage)}
                style={{ opacity: isPaginationDisabled || currentPage === totalPage ? 0.2 : 1, cursor: isPaginationDisabled || currentPage === totalPage ? 'not-allowed' : 'pointer' }}
              >
                <ChevronDoubleRight size={14} />
              </span>
            </i>
          </li>
        </ul>
      </nav>
    </>
  );
}
